import React, {useState, useMemo} from "react";
import {DateObject} from "react-multi-date-picker";
import {ChevronLeft} from "lucide-react";
import PropTypes from "prop-types";
//import "./style.scss"

const SpotGroupOccupancy = (props) => {
  const {
    spotGroup,
    createDateRange,
  } = props

  const [selectedMonth, setSelectedMonth] = useState(null) // null = maandoverzicht, index = detail van die maand
  const today = new DateObject()

  // Helper functie om datum te vergelijken (zonder timezone issues)
  const getDateKey = (dateInput) => {
    const d = new DateObject(dateInput)
    return `${d.year}-${String(d.month.number).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`
  }

  // Bereken maandelijkse statistieken
  const monthlyStats = useMemo(() => {
    const months = []
    const startDate = new DateObject()

    // Maak een lookup map van spot_group_indices voor snelle toegang
    const indicesMap = new Map()
    if (spotGroup.spot_group_indices) {
      spotGroup.spot_group_indices.forEach(item => {
        // Parse de UTC datum en converteer naar lokale datum
        const utcDate = new Date(item.date)
        const localDateKey = `${utcDate.getFullYear()}-${String(utcDate.getMonth() + 1).padStart(2, '0')}-${String(utcDate.getDate()).padStart(2, '0')}`
        indicesMap.set(localDateKey, item)
      })
    }

    for (let i = 0; i < 12; i++) {
      const monthDate = new DateObject(startDate).add(i, "months")
      const monthStart = new DateObject(monthDate).setDay(1)
      const monthEnd = new DateObject(monthDate).toLastOfMonth()

      const daysInMonth = createDateRange([monthStart, monthEnd])
      let totalAvailable = 0
      let totalDays = 0
      let fullyBookedDays = 0

      const dailyData = daysInMonth.map(date => {
        const dateKey = getDateKey(date)
        const spotGroupIndex = indicesMap.get(dateKey)

        // Als er geen data is voor deze dag, is de locatie dicht
        const isOpen = spotGroupIndex !== undefined
        const available = isOpen ? spotGroupIndex.spots_available : 0
        const booked = isOpen ? spotGroup.spot_count - available : 0
        const percent = (isOpen && spotGroup.spot_count > 0)
          ? Math.round((available / spotGroup.spot_count) * 100)
          : 0

        // Alleen open dagen meetellen in statistieken
        if (isOpen) {
          totalAvailable += available
          totalDays++
          if (available === 0) fullyBookedDays++
        }

        return {
          date,
          day: date.day,
          weekDay: date.weekDay.shortName,
          available,
          booked,
          total: spotGroup.spot_count,
          percent,
          isOpen,
          isWeekend: date.weekDay.index === 0 || date.weekDay.index === 6,
          isToday: date.toDays() === today.toDays(),
          isPast: date.toDays() < today.toDays()
        }
      })

      const totalCapacity = totalDays * spotGroup.spot_count
      const avgPercent = totalCapacity > 0
        ? Math.round((totalAvailable / totalCapacity) * 100)
        : 0
      const avgAvailable = totalDays > 0 ? Math.round(totalAvailable / totalDays) : 0

      months.push({
        name: monthDate.month.name,
        shortName: monthDate.month.shortName,
        year: monthDate.year,
        avgAvailable,
        avgPercent,
        fullyBookedDays,
        totalDays,
        isCurrentMonth: monthDate.month.number === today.month.number && monthDate.year === today.year,
        days: dailyData,
        // Voor weergave in grid - voeg lege dagen toe voor uitlijning
        startWeekDay: new DateObject(monthDate).setDay(1).weekDay.index
      })
    }

    return months
  }, [spotGroup, createDateRange])

  const getAvailabilityColor = (percent) => {
    if (percent >= 80) return 'high'
    if (percent >= 40) return 'medium'
    if (percent > 0) return 'low'
    return 'none'
  }

  const selectedMonthData = selectedMonth !== null ? monthlyStats[selectedMonth] : null

  return (
    <div className="component-spot-group-occupancy">
      {/* Header */}
      <div className="occupancy-header">
        <div className="occupancy-info">
          {selectedMonthData ? (
            <button className="back-button" onClick={() => setSelectedMonth(null)}>
              <ChevronLeft /><span>Terug naar jaaroverzicht</span>
            </button>
          ) : (
            <span className="spot-count">{spotGroup.spot_count} standplaats{spotGroup.spot_count !== 1 ? 'en' : ''}</span>
          )}
        </div>
        {/*<div className="legend">*/}
        {/*  <span className="legend-item high">Veel vrij</span>*/}
        {/*  <span className="legend-item medium">Beperkt</span>*/}
        {/*  <span className="legend-item low">Bijna vol</span>*/}
        {/*  <span className="legend-item none">Vol</span>*/}
        {/*  <span className="legend-item closed">Dicht</span>*/}
        {/*</div>*/}
      </div>

      {/* Maandoverzicht */}
      {selectedMonth === null && (
        <div className="months-view">
          {monthlyStats.map((month, index) => (
            <div
              key={index}
              className={`month-card ${getAvailabilityColor(month.avgPercent)} ${month.isCurrentMonth ? 'current' : ''}`}
              onClick={() => setSelectedMonth(index)}
            >
              <div className="month-name">{month.shortName}</div>
              <div className="month-year">{month.year}</div>
              {month.totalDays > 0 ? (
                <>
                  <div className="month-stats">
                    <div className="stat-main">
                      <span className="stat-value">{month.avgPercent}%</span>
                    </div>
                    <span className="stat-label">beschikbaar</span>
                  </div>
                  <div className="month-bar">
                    <div
                      className="month-bar-fill"
                      style={{width: `${month.avgPercent}%`}}
                    />
                  </div>
                  <div className="stat-detail">
                    <span className="stat-open">{month.totalDays} dag{month.totalDays !== 1 ? 'en' : ''} open</span>
                    {month.fullyBookedDays > 0 && (
                      <span className="stat-booked"> · {month.fullyBookedDays} vol</span>
                    )}
                  </div>
                </>
              ) : (
                <div className="month-stats">
                  <div className="stat-main stat-closed">
                    <span className="stat-value">Dicht</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Maand detail view */}
      {selectedMonthData && (
        <div className="month-detail-view">
          <div className="month-detail-header">
            <h4>{selectedMonthData.name} {selectedMonthData.year}</h4>
            <div className="month-summary">
              <span><strong>{selectedMonthData.totalDays}</strong> dagen open</span>
              {selectedMonthData.totalDays > 0 && (
                <span>Gemiddeld <strong>{selectedMonthData.avgAvailable}/{spotGroup.spot_count}</strong> vrij</span>
              )}
              {selectedMonthData.fullyBookedDays > 0 && (
                <span className="booked-info">{selectedMonthData.fullyBookedDays} dag{selectedMonthData.fullyBookedDays !== 1 ? 'en' : ''} vol</span>
              )}
            </div>
          </div>

          {/* Weekdag headers */}
          <div className="weekday-headers">
            <span>Zo</span>
            <span>Ma</span>
            <span>Di</span>
            <span>Wo</span>
            <span>Do</span>
            <span>Vr</span>
            <span>Za</span>
          </div>

          {/* Dagen grid */}
          <div className="days-grid">
            {/* Lege cellen voor uitlijning */}
            {Array.from({length: selectedMonthData.startWeekDay}).map((_, i) => (
              <div key={`empty-${i}`} className="day-card empty" />
            ))}

            {/* Dagen van de maand */}
            {selectedMonthData.days.map((day, index) => (
              <div
                key={index}
                className={`day-card ${day.isOpen ? getAvailabilityColor(day.percent) : 'closed'} ${day.isToday ? 'today' : ''} ${day.isPast ? 'past' : ''}`}
              >
                <div className="day-number">{day.day}</div>
                {day.isOpen ? (
                  <div className="day-available">
                    <span className="available-count">{day.available}</span>
                    <span className="available-total">/{day.total}</span>
                  </div>
                ) : (
                  <div className="day-closed">dicht</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

SpotGroupOccupancy.propTypes = {
  spotGroup: PropTypes.object.isRequired,
  createDateRange: PropTypes.func.isRequired,
}

export default SpotGroupOccupancy
